import React, { useCallback } from 'react';
import BaseTrimHandles from '../BaseTrimHandles';
import { updateClipData } from '../../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { setCurrentTimeWithoutSeeking } from '../../store/remotionPlayerState';
import { getCompositeSegmentById } from '../../clipUpdateDataUtilsWithSplits/compositeUtils';
import { getEndForSegment, getStartForSegment } from '../../playerConfigUtil';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { Clip } from '@/domains/asset';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { MAIN_TRACK_ID } from '@/App/remotion/MainPlayer/utils/playerConfig/constants';

interface TrimHandlesProps {
  fps: number;
  clipId: string;
  clipData: Clip;
  showTrimsBars: 'start' | 'end' | null;
  compositeSegment: CompositeSegment;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  setCompositeSegmentDragStart: (value: number) => void;
  setCompositeSegmentDragEnd: (value: number) => void;
  setCompositeSegmentDragId: (value: string | null) => void;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
}

export default function TrimHandles({
  fps,
  clipId,
  clipData,
  showTrimsBars,
  compositeSegment,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  setCompositeSegmentDragStart,
  setCompositeSegmentDragEnd,
  setCompositeSegmentDragId,
  newTimelineContainerRef,
  speakerTimelineContainerRef
}: TrimHandlesProps): React.ReactElement {
  const { seekTo } = useRemotionPlayerSeek();
  const compositeSegmentStartTime = getStartForSegment(clipData, compositeSegment.id);
  const compositeSegmentEndTime = getEndForSegment(clipData, compositeSegment.id);

  const onBoundsChanged = useCallback(() => {
    setCompositeSegmentDragStart(0);
    setCompositeSegmentDragEnd(0);
    setCompositeSegmentDragId(null);
  }, [setCompositeSegmentDragStart, setCompositeSegmentDragEnd, setCompositeSegmentDragId]);

  const onDragStarted = useCallback(() => {
    setCompositeSegmentDragId(compositeSegment.id);
  }, [compositeSegment.id, setCompositeSegmentDragId]);

  const onStartDragEnd = useCallback(
    (offset: number) => {
      const newTime = roundToNDecimalPlaces(compositeSegmentStartTime + offset, 3);
      if (!compositeSegment) {
        return;
      }
      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
        startTime: newTime,
        type: SegmentType.COMPOSITE,
        compositeId: compositeSegment.id,
        trackId: 'main'
      });
      if (newClipData.asset_metadata.player_config) {
        const updatedSegment = getCompositeSegmentById(newClipData, compositeSegment.id);
        seekTo(roundToNDecimalPlaces(updatedSegment.fromFrame / fps + 0.01, 3));
      }
    },
    [compositeSegmentStartTime, compositeSegment, clipId, fps, seekTo]
  );

  const onEndDragEnd = useCallback(
    (offset: number) => {
      const newTime = roundToNDecimalPlaces(compositeSegmentEndTime + offset, 3);
      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
        endTime: newTime,
        trackId: MAIN_TRACK_ID,
        compositeId: compositeSegment.id,
        type: SegmentType.COMPOSITE
      });
      if (newClipData.asset_metadata.player_config) {
        const updatedSegment = getCompositeSegmentById(newClipData, compositeSegment.id);
        const newSeekTime = updatedSegment.toFrame / fps + 0.01;
        setCurrentTimeWithoutSeeking(newSeekTime);
      }
    },
    [compositeSegmentEndTime, compositeSegment.id, clipId, fps]
  );

  const getLeftTrimBounds = useCallback(() => {
    return {
      minOffset: -(compositeSegment.currentBounds.fromFrame / fps),
      maxOffset: (compositeSegment.currentBounds.toFrame - compositeSegment.currentBounds.fromFrame) / fps
    };
  }, [compositeSegment.currentBounds.fromFrame, compositeSegment.currentBounds.toFrame, fps]);

  const getRightTrimBounds = useCallback(() => {
    return {
      minOffset: -((compositeSegment.currentBounds.toFrame - compositeSegment.currentBounds.fromFrame) / fps),
      maxOffset: (compositeSegment.maxBounds.toFrame - compositeSegment.currentBounds.toFrame) / fps
    };
  }, [
    compositeSegment.currentBounds.fromFrame,
    compositeSegment.currentBounds.toFrame,
    compositeSegment.maxBounds.toFrame,
    fps
  ]);

  const dispatchDeleteChange = useCallback(
    (type: 'add' | 'remove', startTime: number, endTime: number) => {
      EventBus.dispatch(CustomEvents.TimelineDeleteChangeDuringDrag, {
        type,
        startTime: Math.min(startTime, endTime),
        endTime: Math.max(startTime, endTime),
        compositeSegmentId: compositeSegment.id
      });
    },
    [compositeSegment.id]
  );

  const onDragUpdate = useCallback(
    (dragValue: number, baseOffset: number, isStartHandle: boolean) => {
      const adjustedTime = baseOffset + dragValue;
      const shouldAdd = (isStartHandle && dragValue > 0) || (!isStartHandle && dragValue < 0);
      const type = shouldAdd ? 'add' : 'remove';
      dispatchDeleteChange(type, baseOffset, adjustedTime);
    },
    [dispatchDeleteChange]
  );

  const onDragIdle = useCallback(() => {
    EventBus.dispatch(CustomEvents.TimelineDeleteChangeDuringDrag, {
      type: 'reset'
    });
  }, []);

  return (
    <BaseTrimHandles
      fps={fps}
      clipData={clipData}
      showTrimsBars={showTrimsBars}
      segmentId={compositeSegment.id}
      dragStart={compositeSegmentDragStart}
      dragEnd={compositeSegmentDragEnd}
      setDragStart={setCompositeSegmentDragStart}
      setDragEnd={setCompositeSegmentDragEnd}
      newTimelineContainerRef={newTimelineContainerRef}
      speakerTimelineContainerRef={speakerTimelineContainerRef}
      currentBoundsFromFrame={compositeSegment.currentBounds.fromFrame}
      currentBoundsToFrame={compositeSegment.currentBounds.toFrame}
      resetMode="always"
      segmentStartBound={compositeSegmentStartTime}
      segmentEndBound={compositeSegmentEndTime}
      onBoundsChanged={onBoundsChanged}
      onDragStarted={onDragStarted}
      onStartDragEnd={onStartDragEnd}
      onEndDragEnd={onEndDragEnd}
      getLeftTrimBounds={getLeftTrimBounds}
      getRightTrimBounds={getRightTrimBounds}
      onDragUpdate={onDragUpdate}
      onDragIdle={onDragIdle}
    />
  );
}
